import React, { useCallback, useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Pencil, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { leiderschapApi } from '../api';
import { ACTION_ITEM_PRIORITIES, ACTION_ITEM_STATUSES, findClassName, findLabel } from '../labels';
import type {
  ActionItem,
  ActionItemInput,
  ActionItemPriority,
  ActionItemStatus,
  AgendaItem,
  LeadershipMember,
} from '../types';

interface Props {
  meetingId: string;
  item: AgendaItem;
  position: number;
  canManage: boolean;
  currentUserId?: string;
  members: LeadershipMember[];
  onClose: () => void;
  onItemSaved: (item: AgendaItem) => void;
}

interface ActionForm {
  title: string;
  description: string;
  assignee_user_id: string;
  due_date: string;
  status: ActionItemStatus;
  priority: ActionItemPriority;
}

const EMPTY_ACTION_FORM: ActionForm = {
  title: '',
  description: '',
  assignee_user_id: '',
  due_date: '',
  status: 'open',
  priority: 'normaal',
};

// Native selects: reliable on mobile and fully labelled without extra widgets.
const SELECT_CLASS =
  'h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

const errorMessage = (error: any, fallback: string) => error?.response?.data?.detail || fallback;

/** Right-hand detail sheet for one agenda item: information, agenda-item
 * notes and the canonical action items linked to it. Remounted per item
 * (keyed by the caller) so drafts never leak between agenda points. */
export default function AgendaItemSheet({
  meetingId, item, position, canManage, currentUserId, members, onClose, onItemSaved,
}: Props) {
  const [title, setTitle] = useState(item.title);
  const [description, setDescription] = useState(item.description || '');
  const [infoSaving, setInfoSaving] = useState(false);
  const [infoError, setInfoError] = useState<string | null>(null);
  const [infoSaved, setInfoSaved] = useState(false);

  const [minutes, setMinutes] = useState(item.minutes || '');
  const [minutesSaving, setMinutesSaving] = useState(false);
  const [minutesError, setMinutesError] = useState<string | null>(null);
  const [minutesSaved, setMinutesSaved] = useState(false);

  const [actionItems, setActionItems] = useState<ActionItem[]>([]);
  const [actionsLoading, setActionsLoading] = useState(true);
  const [actionsError, setActionsError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ActionForm>(EMPTY_ACTION_FORM);
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const loadActionItems = useCallback(async () => {
    try {
      setActionItems(await leiderschapApi.listActionItems(item.id));
      setActionsError(null);
    } catch (error) {
      setActionsError('Kon actiepunten niet laden');
    } finally {
      setActionsLoading(false);
    }
  }, [item.id]);

  useEffect(() => {
    loadActionItems();
  }, [loadActionItems]);

  const memberName = (userId: string | null) => members.find((m) => m.id === userId)?.name || '-';
  const canEditAction = (action: ActionItem) => canManage || action.assignee_user_id === currentUserId;

  const saveInfo = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!title.trim()) {
      setInfoError('Titel is verplicht');
      return;
    }
    setInfoSaving(true);
    setInfoError(null);
    setInfoSaved(false);
    try {
      const updated = await leiderschapApi.updateAgendaItem(meetingId, item.id, {
        title: title.trim(),
        description,
      });
      onItemSaved(updated);
      setInfoSaved(true);
    } catch (error) {
      // Drafts stay in state so nothing typed is lost.
      setInfoError(errorMessage(error, 'Informatie opslaan mislukt'));
    } finally {
      setInfoSaving(false);
    }
  };

  const saveMinutes = async (event: React.FormEvent) => {
    event.preventDefault();
    setMinutesSaving(true);
    setMinutesError(null);
    setMinutesSaved(false);
    try {
      // Agenda-item notes only; the meeting-level Minutes document is untouched.
      const updated = await leiderschapApi.updateAgendaItem(meetingId, item.id, { minutes });
      onItemSaved(updated);
      setMinutesSaved(true);
    } catch (error) {
      setMinutesError(errorMessage(error, 'Notulen opslaan mislukt'));
    } finally {
      setMinutesSaving(false);
    }
  };

  const openCreateForm = () => {
    setEditingId(null);
    setForm(EMPTY_ACTION_FORM);
    setFormError(null);
    setFormOpen(true);
  };

  const openEditForm = (action: ActionItem) => {
    setEditingId(action.id);
    setForm({
      title: action.title,
      description: action.description || '',
      assignee_user_id: action.assignee_user_id || '',
      due_date: action.due_date || '',
      status: action.status,
      priority: action.priority,
    });
    setFormError(null);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
    setFormError(null);
  };

  const saveAction = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.title.trim()) {
      setFormError('Titel is verplicht');
      return;
    }
    if (!editingId && !form.assignee_user_id) {
      setFormError('Kies een actiehouder');
      return;
    }
    const payload: ActionItemInput = {
      title: form.title.trim(),
      description: form.description || null,
      assignee_user_id: form.assignee_user_id || null,
      due_date: form.due_date || null,
      status: form.status,
      priority: form.priority,
    };
    setFormSaving(true);
    setFormError(null);
    try {
      if (editingId) {
        await leiderschapApi.updateActionItem(editingId, payload);
        toast.success('Actiepunt bijgewerkt');
      } else {
        // Meeting and agenda item are bound here, never chosen by the user.
        await leiderschapApi.createActionItem({ ...payload, meeting_id: meetingId, agenda_item_id: item.id });
        toast.success('Actiepunt aangemaakt');
      }
      closeForm();
      await loadActionItems();
    } catch (error) {
      // Keep the form open with everything the user entered.
      setFormError(errorMessage(error, 'Actiepunt opslaan mislukt'));
    } finally {
      setFormSaving(false);
    }
  };

  return (
    <Sheet open onOpenChange={(open: boolean) => { if (!open) onClose(); }}>
      <SheetContent
        className="w-full max-w-full overflow-y-auto overflow-x-hidden p-4 sm:w-[36rem] sm:max-w-[36rem] sm:p-6"
        data-testid="agenda-item-sheet"
      >
        <SheetHeader className="pr-8 text-left">
          <SheetTitle className="break-words">{position}. {item.title}</SheetTitle>
          <SheetDescription className="sr-only">
            Informatie, notulen en actiepunten van dit agendapunt
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-6">
          <form onSubmit={saveInfo} className="space-y-3" aria-labelledby="agenda-info-heading">
            <h3 id="agenda-info-heading" className="text-xs font-semibold uppercase tracking-wide text-slate-500">Informatie</h3>
            <div>
              <Label htmlFor="agenda-item-title">Titel</Label>
              <Input
                id="agenda-item-title"
                value={title}
                disabled={!canManage}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setTitle(e.target.value); setInfoSaved(false); }}
              />
            </div>
            <div>
              <Label htmlFor="agenda-item-description">Informatie / voorbereiding</Label>
              <Textarea
                id="agenda-item-description"
                rows={4}
                value={description}
                disabled={!canManage}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => { setDescription(e.target.value); setInfoSaved(false); }}
              />
            </div>
            {infoError && <p role="alert" className="text-sm text-red-600">{infoError}</p>}
            {infoSaved && <p role="status" className="text-sm text-emerald-600">Informatie opgeslagen</p>}
            {canManage && (
              <Button type="submit" disabled={infoSaving}>{infoSaving ? 'Opslaan...' : 'Informatie opslaan'}</Button>
            )}
          </form>

          <form onSubmit={saveMinutes} className="space-y-3" aria-labelledby="agenda-minutes-heading">
            <h3 id="agenda-minutes-heading" className="text-xs font-semibold uppercase tracking-wide text-slate-500">Notulen</h3>
            <div>
              <Label htmlFor="agenda-item-minutes" className="sr-only">Notulen van dit agendapunt</Label>
              <Textarea
                id="agenda-item-minutes"
                rows={6}
                placeholder="Wat is bij dit agendapunt besproken?"
                value={minutes}
                disabled={!canManage}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => { setMinutes(e.target.value); setMinutesSaved(false); }}
              />
            </div>
            {minutesError && <p role="alert" className="text-sm text-red-600">{minutesError}</p>}
            {minutesSaved && <p role="status" className="text-sm text-emerald-600">Notulen opgeslagen</p>}
            {canManage && (
              <Button type="submit" disabled={minutesSaving}>{minutesSaving ? 'Opslaan...' : 'Notulen opslaan'}</Button>
            )}
          </form>

          <section aria-labelledby="agenda-actions-heading" className="space-y-3">
            <h3 id="agenda-actions-heading" className="text-xs font-semibold uppercase tracking-wide text-slate-500">Actiepunten</h3>
            {actionsLoading && <p className="text-sm text-slate-500">Laden...</p>}
            {actionsError && <p role="alert" className="text-sm text-red-600">{actionsError}</p>}
            {!actionsLoading && !actionsError && actionItems.length === 0 && (
              <p className="text-sm text-slate-500">Nog geen actiepunten bij dit agendapunt.</p>
            )}
            <ul className="space-y-2">
              {actionItems.map((action) => (
                <li key={action.id} className="flex items-start gap-2 rounded-lg border border-slate-100 p-3" data-testid="agenda-action-item">
                  <div className="min-w-0 flex-1 space-y-1">
                    <p className="break-words text-sm font-medium text-slate-900">{action.title}</p>
                    <p className="break-words text-xs text-slate-500">Toegewezen aan: {memberName(action.assignee_user_id)}</p>
                    <p className="text-xs text-slate-500">
                      Deadline: {action.due_date ? format(parseISO(action.due_date), 'dd-MM-yyyy') : '-'}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      <Badge className={findClassName(ACTION_ITEM_STATUSES, action.status)}>
                        Status: {findLabel(ACTION_ITEM_STATUSES, action.status)}
                      </Badge>
                      <Badge className={findClassName(ACTION_ITEM_PRIORITIES, action.priority)}>
                        Prioriteit: {findLabel(ACTION_ITEM_PRIORITIES, action.priority)}
                      </Badge>
                    </div>
                  </div>
                  {canEditAction(action) && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditForm(action)}
                      aria-label={`Actiepunt bewerken: ${action.title}`}
                    >
                      <Pencil className="h-4 w-4 text-slate-400" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>

            {formOpen && (
              <form onSubmit={saveAction} className="space-y-3 rounded-lg border border-slate-200 p-3" aria-label={editingId ? 'Actiepunt bewerken' : 'Nieuw actiepunt'}>
                <div>
                  <Label htmlFor="agenda-action-title">Titel</Label>
                  <Input
                    id="agenda-action-title"
                    value={form.title}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, title: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="agenda-action-description">Omschrijving</Label>
                  <Textarea
                    id="agenda-action-description"
                    rows={2}
                    value={form.description}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setForm({ ...form, description: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="min-w-0">
                    <Label htmlFor="agenda-action-assignee">Toegewezen aan</Label>
                    <select
                      id="agenda-action-assignee"
                      className={SELECT_CLASS}
                      value={form.assignee_user_id}
                      onChange={(e) => setForm({ ...form, assignee_user_id: e.target.value })}
                    >
                      <option value="">Kies een leiderschapslid</option>
                      {members.map((member) => (
                        <option key={member.id} value={member.id}>{member.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="min-w-0">
                    <Label htmlFor="agenda-action-due">Deadline</Label>
                    <Input
                      id="agenda-action-due"
                      type="date"
                      value={form.due_date}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, due_date: e.target.value })}
                    />
                  </div>
                  <div className="min-w-0">
                    <Label htmlFor="agenda-action-status">Status</Label>
                    <select
                      id="agenda-action-status"
                      className={SELECT_CLASS}
                      value={form.status}
                      onChange={(e) => setForm({ ...form, status: e.target.value as ActionItemStatus })}
                    >
                      {ACTION_ITEM_STATUSES.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="min-w-0">
                    <Label htmlFor="agenda-action-priority">Prioriteit</Label>
                    <select
                      id="agenda-action-priority"
                      className={SELECT_CLASS}
                      value={form.priority}
                      onChange={(e) => setForm({ ...form, priority: e.target.value as ActionItemPriority })}
                    >
                      {ACTION_ITEM_PRIORITIES.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {formError && <p role="alert" className="text-sm text-red-600">{formError}</p>}
                <div className="flex flex-wrap gap-2">
                  <Button type="submit" disabled={formSaving}>
                    {formSaving ? 'Opslaan...' : editingId ? 'Actiepunt opslaan' : 'Actiepunt aanmaken'}
                  </Button>
                  <Button type="button" variant="outline" onClick={closeForm}>Annuleren</Button>
                </div>
              </form>
            )}

            {canManage && !formOpen && (
              <Button type="button" variant="outline" onClick={openCreateForm}>
                <Plus className="mr-2 h-4 w-4" />
                Actiepunt toevoegen
              </Button>
            )}
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
}
